<template>
  <div 
    class="link-bio-page min-h-screen w-full"
    :style="{
      backgroundColor: page.background_color,
      backgroundImage: page.background_image ? `url(${page.background_image})` : 'none',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed'
    }"
  >
    <div class="container mx-auto px-4 sm:px-6 py-8 sm:py-12 lg:py-16 max-w-md">
      <!-- Profile Section -->
      <div class="text-center mb-8 sm:mb-10" :style="{ color: page.text_color }">
        <div class="mb-6 sm:mb-8 relative inline-block">
          <div class="relative">
            <img 
              v-if="page.profile_picture" 
              :src="page.profile_picture" 
              :alt="page.name || 'Profile'" 
              class="w-28 h-28 sm:w-36 sm:h-36 rounded-full object-cover mx-auto border-4 border-white shadow-xl"
              :style="{ boxShadow: `0 0 0 4px ${page.button_color}20` }"
              @error="$event.target.style.display = 'none'"
            />
            <div 
              v-if="page.profile_picture" 
              class="w-28 h-28 sm:w-36 sm:h-36 rounded-full bg-gray-200 mx-auto border-4 border-white shadow-xl flex items-center justify-center absolute inset-0"
              style="display: none;"
            >
              <icon name="user" class="w-14 h-14 sm:w-18 sm:h-18 text-gray-400" />
            </div>
            <div v-else class="w-28 h-28 sm:w-36 sm:h-36 rounded-full bg-white mx-auto border-4 border-white shadow-xl flex items-center justify-center">
              <icon name="user" class="w-14 h-14 sm:w-18 sm:h-18 text-gray-400" />
            </div>
          </div>
        </div>
        
        <h1 v-if="page.name" class="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2 sm:mb-3">{{ page.name }}</h1>
        <p v-if="page.title" class="text-base sm:text-lg lg:text-xl opacity-90 mb-3 sm:mb-4 font-medium">{{ page.title }}</p>
        <p v-if="page.bio" class="text-sm sm:text-base lg:text-lg opacity-80 leading-relaxed max-w-lg mx-auto px-4">{{ page.bio }}</p>
      </div>
      
      <!-- Links Section -->
      <div class="space-y-3 sm:space-y-4 mb-8 sm:mb-10">
        <a 
          v-for="(link, index) in pageLinks" 
          :key="index"
          :href="formatUrl(link.url)" 
          target="_blank"
          rel="noopener noreferrer"
          class="group block w-full py-4 sm:py-5 px-5 sm:px-6 text-center font-semibold text-white transition-all hover:opacity-95 hover:shadow-2xl transform hover:scale-[1.02] active:scale-[0.98] text-sm sm:text-base rounded-lg flex items-center justify-center gap-3"
          :class="getButtonClass()"
          :style="{
            backgroundColor: page.button_color
          }"
        >
          <span class="flex-1">{{ link.title || `Link ${index + 1}` }}</span>
          <icon name="arrow-right" class="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity transform group-hover:translate-x-1" />
        </a>
        
        <div v-if="pageLinks.length === 0" class="text-center py-16 sm:py-20 border-2 border-dashed rounded-lg bg-white bg-opacity-10 backdrop-blur-sm" :style="{ borderColor: page.text_color + '30', color: page.text_color }">
          <div class="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 bg-white bg-opacity-10 rounded-full flex items-center justify-center">
            <icon name="website" class="w-8 h-8 sm:w-10 sm:h-10 opacity-50" />
          </div>
          <p class="text-sm sm:text-base opacity-70 font-medium">No links available</p>
          <p class="text-xs sm:text-sm opacity-50 mt-1">Links will appear here when added</p>
        </div>
      </div>

      <!-- Lead Capture Form -->
      <div v-if="enableLeadCapture" class="mb-8 sm:mb-10">
        <ContactForm
          :profile-id="page.id"
          :form-type="leadFormType"
          :button-color="page.button_color"
          :text-color="page.text_color"
          :border-color="page.text_color + '20'"
        />
      </div>
      
      <!-- Footer -->
      <div class="mt-12 sm:mt-16 text-center" :style="{ color: page.text_color }">
        <p class="text-xs sm:text-sm opacity-60">Powered by Link-in-Bio Generator</p>
      </div>
    </div>
  </div>
</template>

<script>
import Icon from '@/Shared/Icon.vue'
import ContactForm from '@/Shared/ContactForm.vue'

export default {
  components: {
    Icon,
    ContactForm,
  },
  props: {
    page: {
      type: Object,
      required: true,
    },
    title: {
      type: String,
      default: 'Link in Bio',
    },
    enableLeadCapture: {
      type: Boolean,
      default: false,
    },
    leadFormType: {
      type: String,
      default: 'email_only',
    },
  },
  computed: {
    pageLinks() {
      if (!this.page.links) return []
      if (typeof this.page.links === 'string') {
        try {
          return JSON.parse(this.page.links)
        } catch (e) {
          return []
        }
      }
      return this.page.links
    },
  },
  methods: {
    formatUrl(url) {
      if (!url) return '#'
      if (url.indexOf('://') === -1) {
        return 'https://' + url
      }
      return url
    },
    getButtonClass() {
      switch(this.page.button_style) {
        case 'square':
          return 'rounded-none'
        case 'pill':
          return 'rounded-full'
        case 'rounded':
        default:
          return 'rounded-lg'
      }
    },
  },
}
</script>

<style scoped>
.link-bio-page {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
}
</style>

